import React, { useState, useEffect } from 'react';
import { Plus, Check, Clock, Calendar, UserCheck, Search, Filter, X, Loader2, ListChecks, Trash2, Download, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_CONFIG = {
  high: { label: 'High', color: 'bg-red-100 text-red-700' },
  medium: { label: 'Medium', color: 'bg-amber-100 text-amber-700' },
  low: { label: 'Low', color: 'bg-green-100 text-green-700' },
};

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-blue-100 text-blue-700', icon: Clock },
  pending_approval: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700', icon: Check },
};

const TYPE_CONFIG = {
  self: { label: 'Self', color: 'text-gray-500' },
  manager: { label: 'Manager', color: 'text-violet-600' },
  admin: { label: 'Admin', color: 'text-red-600' },
};

const TodoHistory = () => {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showAssign, setShowAssign] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    employee_id: '',
    department: '',
    search: '',
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isViewer = user.role === 'viewer' || user.role === 'report_viewer';

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.employee_id) params.append('employee_id', filters.employee_id);
      if (filters.department) params.append('department', filters.department);

      const [todosRes, deptsRes, empsRes] = await Promise.all([
        axios.get(`${API}/api/todo-manager/all?${params}`, { headers }),
        axios.get(`${API}/api/todo-manager/departments`, { headers }),
        axios.get(`${API}/api/todo-manager/employees-list`, { headers }),
      ]);
      setTodos(todosRes.data.todos || []);
      setDepartments(deptsRes.data || []);
      setEmployees(empsRes.data || []);
    } catch (err) {
      console.error('Failed to load todos:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [filters.status, filters.employee_id, filters.department]);

  const handleDelete = async (todoId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    setDeleting(todoId);
    try {
      await axios.delete(`${API}/api/todo-manager/${todoId}`, { headers });
      setTodos(prev => prev.filter(t => t.id !== todoId));
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(null);
    }
  };

  const handleDownload = async (type, id = '') => {
    setDownloading(type + id);
    try {
      let url;
      if (type === 'task') url = `${API}/api/todo-manager/download/task/${id}`;
      else if (type === 'department') url = `${API}/api/todo-manager/download/department/${encodeURIComponent(id)}`;
      else url = `${API}/api/todo-manager/download/all`;

      const res = await axios.get(url, { headers, responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const disposition = res.headers['content-disposition'];
      const filename = disposition ? disposition.split('filename=')[1] : `tasks_report.pdf`;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      alert('Failed to download PDF');
    } finally {
      setDownloading(null);
    }
  };

  const filteredTodos = todos.filter(t => {
    if (!filters.search) return true;
    const s = filters.search.toLowerCase();
    return (t.title?.toLowerCase().includes(s) || t.employee_name?.toLowerCase().includes(s) || t.employee_code?.toLowerCase().includes(s));
  });

  const activeCount = todos.filter(t => t.status === 'active' || t.status === 'pending_approval').length;
  const completedCount = todos.filter(t => t.status === 'completed').length;

  return (
    <div className="space-y-6" data-testid="todo-history">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">To-Do List History</h1>
          <p className="text-sm text-gray-500 mt-1">Track and manage all employee tasks</p>
        </div>
        <div className="flex items-center gap-2">
          {/* PDF Download Buttons */}
          {filters.department && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDownload('department', filters.department)}
              disabled={!!downloading}
              data-testid="download-dept-pdf"
            >
              {downloading === `department${filters.department}` ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileText className="h-4 w-4 mr-1 text-orange-600" />}
              {filters.department} PDF
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleDownload('all')}
            disabled={!!downloading}
            data-testid="download-all-pdf"
          >
            {downloading === 'all' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1 text-violet-600" />}
            All Tasks PDF
          </Button>
          {!isViewer && (
            <Button className="bg-violet-600 hover:bg-violet-700" size="sm" onClick={() => setShowAssign(true)} data-testid="admin-assign-btn">
              <Plus className="h-4 w-4 mr-1" /> Assign Task
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-gray-900">{todos.length}</p>
            <p className="text-xs text-gray-500 mt-1">Total Tasks</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-blue-600">{activeCount}</p>
            <p className="text-xs text-gray-500 mt-1">Active</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-emerald-600">{completedCount}</p>
            <p className="text-xs text-gray-500 mt-1">Completed</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search tasks or employees..."
                value={filters.search}
                onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                className="pl-9"
                data-testid="todo-search-input"
              />
            </div>
            <Select value={filters.status} onValueChange={v => setFilters(f => ({ ...f, status: v === 'all' ? '' : v }))}>
              <SelectTrigger data-testid="todo-status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="pending_approval">Pending Approval</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.department} onValueChange={v => setFilters(f => ({ ...f, department: v === 'all' ? '' : v }))}>
              <SelectTrigger data-testid="todo-dept-filter">
                <SelectValue placeholder="All Departments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map(d => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.employee_id} onValueChange={v => setFilters(f => ({ ...f, employee_id: v === 'all' ? '' : v }))}>
              <SelectTrigger data-testid="todo-employee-filter">
                <SelectValue placeholder="All Employees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Employees</SelectItem>
                {employees.map(e => (
                  <SelectItem key={e.id} value={e.id}>{e.name} ({e.employee_code})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tasks Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
            </div>
          ) : filteredTodos.length === 0 ? (
            <div className="py-16 text-center">
              <ListChecks className="h-12 w-12 mx-auto mb-3 text-gray-200" />
              <p className="text-gray-400">No tasks found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Employee</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Task</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Priority</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Assigned By</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Due Date</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Created</th>
                    {!isViewer && <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredTodos.map(todo => {
                    const pCfg = PRIORITY_CONFIG[todo.priority] || PRIORITY_CONFIG.medium;
                    const sCfg = STATUS_CONFIG[todo.status] || STATUS_CONFIG.active;
                    const tCfg = TYPE_CONFIG[todo.assigned_by_type] || TYPE_CONFIG.self;
                    const isOverdue = todo.due_date && todo.status === 'active' && new Date(todo.due_date + 'T23:59:59') < new Date();

                    return (
                      <tr key={todo.id} className="hover:bg-gray-50 transition" data-testid={`history-todo-${todo.id}`}>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-gray-900">{todo.employee_name}</p>
                          <p className="text-xs text-gray-400">{todo.employee_code} - {todo.employee_department}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-gray-900 max-w-xs truncate">{todo.title}</p>
                          {todo.description && (
                            <p className="text-xs text-gray-400 truncate max-w-xs">{todo.description}</p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`text-xs ${pCfg.color}`}>{pCfg.label}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`text-xs ${sCfg.color}`}>{sCfg.label}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-medium ${tCfg.color}`}>
                            {todo.assigned_by_type === 'self' ? 'Self' : todo.assigned_by_name || tCfg.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {todo.due_date ? (
                            <span className={`text-xs ${isOverdue ? 'text-red-500 font-semibold' : 'text-gray-500'}`}>
                              {new Date(todo.due_date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                              {isOverdue && ' (overdue)'}
                            </span>
                          ) : <span className="text-xs text-gray-300">-</span>}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-gray-400">
                            {new Date(todo.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </span>
                        </td>
                        {!isViewer && (
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0"
                                onClick={() => handleDownload('task', todo.id)}
                                disabled={downloading === `task${todo.id}`}
                                title="Download PDF"
                                data-testid={`download-task-${todo.id}`}
                              >
                                {downloading === `task${todo.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5 text-violet-500" />}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0"
                                onClick={() => handleDelete(todo.id)}
                                disabled={deleting === todo.id}
                                title="Delete task"
                                data-testid={`delete-task-${todo.id}`}
                              >
                                {deleting === todo.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 text-red-500" />}
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Admin Assign Task Dialog */}
      {showAssign && (
        <AdminAssignDialog
          employees={employees}
          headers={headers}
          onClose={() => setShowAssign(false)}
          onSuccess={() => { setShowAssign(false); loadData(); }}
        />
      )}
    </div>
  );
};

/* ── Admin Assign Task Dialog ────────────────── */
const AdminAssignDialog = ({ employees, headers, onClose, onSuccess }) => {
  const [employeeId, setEmployeeId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState('medium');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredEmployees = employees.filter(e => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return e.name.toLowerCase().includes(s) || e.employee_code.toLowerCase().includes(s) || e.department?.toLowerCase().includes(s);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!employeeId) { setError('Select an employee'); return; }
    if (!title.trim()) { setError('Title is required'); return; }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/api/todo-manager/assign`, {
        employee_id: employeeId,
        title: title.trim(),
        description,
        due_date: dueDate || null,
        priority,
      }, { headers });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to assign task');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedEmp = employees.find(e => e.id === employeeId);

  return (
    <Dialog open={true} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-violet-600" />
            Assign Task to Employee
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-sm font-medium">Assign To *</Label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger className="mt-1" data-testid="admin-assign-employee">
                <SelectValue placeholder="Select employee" />
              </SelectTrigger>
              <SelectContent>
                {filteredEmployees.map(emp => (
                  <SelectItem key={emp.id} value={emp.id}>{emp.name} ({emp.employee_code}) - {emp.department}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-medium">Title *</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Task title" className="mt-1" data-testid="admin-assign-title" />
          </div>
          <div>
            <Label className="text-sm font-medium">Description</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Details..." rows={2} className="mt-1" data-testid="admin-assign-desc" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium">Due Date</Label>
              <Input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className="mt-1" data-testid="admin-assign-due" />
            </div>
            <div>
              <Label className="text-sm font-medium">Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="mt-1" data-testid="admin-assign-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {error && <p className="text-sm text-red-500 text-center">{error}</p>}
          <Button type="submit" className="w-full bg-violet-600 hover:bg-violet-700" disabled={submitting || !title.trim() || !employeeId} data-testid="admin-submit-assign">
            {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Assigning...</> : <><Plus className="h-4 w-4 mr-2" /> Assign Task</>}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TodoHistory;
